import { useState, useEffect } from 'react';
import { PrinterType } from '../../Components/ReusableComponents/PrinterConfiguration/types';
import { PrintRequestStatus } from '../Api/asn';
import { useUpdatePrintRequestStatus } from './printRequestStatus.hooks';

export interface ZplCmdInfo {
  totalLabel: number;
  id: string;
  labelType: string;
}

export interface BrowserPrintConfig {
  name: string;
  connection: string;
  deviceType: string;
  manufacturer: string;
  provider: string;
  uid: string;
}

export interface BrowserPrintInstance {
  name: string;
  deviceType: string;
  connection: string;
  uid: string;
  version: number;
  provider: string;
  manufacturer: string;
  readRetries: number;

  /*
    Methods types have been extracted from minified code version and are not official
    They are subject to change and might not be 100% perfect
  */
  /* eslint-disable @typescript-eslint/no-explicit-any */
  sendErrorCallback: (error: any) => void;
  sendFinishedCallback: (response: any) => void;
  readErrorCallback: (error: any) => void;
  readFinishedCallback: (response: any) => void;

  send: (
    data: any,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
  ) => void;
  sendFile: (
    data: Blob | string,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
  ) => void;
  sendUrl: (
    url: string,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    options?: any,
  ) => void;
  read: (
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
  ) => void;
  readUntilStringReceived: (
    delimiter: string,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    retries?: number,
    accumData?: string,
  ) => void;
  readAllAvailable: (
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    retries?: number,
  ) => void;
  sendThenRead: (
    data: any,
    readSuccessCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
  ) => void;
  sendThenReadAllAvailable: (
    data: any,
    readSuccessCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    retries?: number,
  ) => void;
  sendThenReadUntilStringReceived: (
    data: any,
    delimiter: string,
    readSuccessCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
    retries?: number,
    accumData?: string,
  ) => void;
  convertAndSendFile: (
    file: Blob | string,
    convertOptions: any,
    successCallback?: (response: any) => void,
    errorCallback?: (error: any) => void,
  ) => void;
}

declare global {
  interface Window {
    BrowserPrint: any;
  }
}

let devicesCache: BrowserPrintInstance[] | null = null;

export type PrinterStatus = 'not selected' | 'selected' | 'ready' | 'offline';

interface Printer {
  config: BrowserPrintConfig | null;
  device: BrowserPrintInstance | null;
  status: PrinterStatus;
}

const useBrowserPrint = () => {
  const [deviceList, setDevices] = useState<BrowserPrintInstance[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<
    Record<PrinterType, Printer>
  >({
    unitLabel: {
      config: null,
      device: null,
      status: 'not selected',
    },
    hangTag: {
      config: null,
      device: null,
      status: 'not selected',
    },
    cartonLabel: {
      config: null,
      device: null,
      status: 'not selected',
    },
  });
  const updatePrintRequestStatus = useUpdatePrintRequestStatus();

  const [loading, setLoading] = useState<boolean>(true);

  const clearPrintersConfig = (printerType?: PrinterType) => {
    if (printerType === 'unitLabel') {
      localStorage.removeItem('unitLabelPrinterConfig');
      setSelectedDevices((prevState) => ({
        ...prevState,
        unitLabel: { config: null, device: null, status: 'not selected' },
      }));
    } else if (printerType === 'hangTag') {
      localStorage.removeItem('hangTagPrinterConfig');
      setSelectedDevices((prevState) => ({
        ...prevState,
        hangTag: { config: null, device: null, status: 'not selected' },
      }));
    } else if (printerType === 'cartonLabel') {
      localStorage.removeItem('cartonLabelPrinterConfig');
      setSelectedDevices((prevState) => ({
        ...prevState,
        cartonLabel: { config: null, device: null, status: 'not selected' },
      }));
    } else {
      localStorage.removeItem('unitLabelPrinterConfig');
      localStorage.removeItem('hangTagPrinterConfig');
      setSelectedDevices({
        unitLabel: { config: null, device: null, status: 'not selected' },
        hangTag: { config: null, device: null, status: 'not selected' },
        cartonLabel: { config: null, device: null, status: 'not selected' },
      });
    }
  };

  /* Ensure BrowserPrint is loaded */
  useEffect(() => {
    if (!window.BrowserPrint) {
      const scriptBP = document.createElement('script');
      scriptBP.src = `${process.env.PUBLIC_URL}/BrowserPrint-3.1.250.min.js`;
      scriptBP.onload = () => console.log('BrowserPrint loaded successfully.');
      scriptBP.onerror = () => console.error('Failed to load BrowserPrint.');

      const scriptBPZ = document.createElement('script');
      scriptBPZ.src = `${process.env.PUBLIC_URL}/BrowserPrint-Zebra-1.1.250.min.js`;
      scriptBPZ.onload = () =>
        console.log('BrowserPrint-Zebra loaded successfully.');
      scriptBPZ.onerror = () =>
        console.error('Failed to load BrowserPrint-Zebra.');

      document.body.appendChild(scriptBP);
      document.body.appendChild(scriptBPZ);
    } else {
      console.log('BrowserPrint is already loaded.');
    }
  }, []);

  /* Retrieve all local devices */
  useEffect(() => {
    setLoading(true);
    if (devicesCache !== null) {
      setDevices(devicesCache);
      setLoading(false);
    } else {
      if (window.BrowserPrint === undefined) {
        setLoading(false);
        return;
      }
      window.BrowserPrint.getLocalDevices(
        ({ printer }: { printer: BrowserPrintInstance[] }) => {
          if (printer) {
            devicesCache = printer;
            setDevices(printer);
          }
          setLoading(false);
        },
        (err: any) => {
          console.error(err);
          setLoading(false);
        },
      );
    }
  }, [window.BrowserPrint]);

  /* Retrieve local storage config */
  useEffect(() => {
    const unitLabelPrinterConfig = localStorage.getItem(
      'unitLabelPrinterConfig',
    );
    const hangTagPrinterConfig = localStorage.getItem('hangTagPrinterConfig');
    const cartonLabelPrinterConfig = localStorage.getItem(
      'cartonLabelPrinterConfig',
    );

    const parsedUnitLabelPrinterConfig = unitLabelPrinterConfig
      ? JSON.parse(unitLabelPrinterConfig)
      : unitLabelPrinterConfig;
    const parsedHangtagPrinterConfig = hangTagPrinterConfig
      ? JSON.parse(hangTagPrinterConfig)
      : hangTagPrinterConfig;
    const parsedCartonLabelPrinterConfig = cartonLabelPrinterConfig
      ? JSON.parse(cartonLabelPrinterConfig)
      : cartonLabelPrinterConfig;

    setSelectedDevices({
      unitLabel: {
        config: parsedUnitLabelPrinterConfig,
        device: null,
        status: parsedUnitLabelPrinterConfig ? 'selected' : 'not selected',
      },
      hangTag: {
        config: parsedHangtagPrinterConfig,
        device: null,
        status: parsedHangtagPrinterConfig ? 'selected' : 'not selected',
      },
      cartonLabel: {
        config: parsedCartonLabelPrinterConfig,
        device: null,
        status: parsedCartonLabelPrinterConfig ? 'selected' : 'not selected',
      },
    });
  }, []);

  /* Retrieve devices for a given printer type */
  const retrieveDevices = (printerType: PrinterType) => {
    if (deviceList.length === 0) return;
    const { config } = selectedDevices[printerType];
    if (config) {
      const deviceFound = deviceList.find(
        (device) => device.name === config.name,
      );
      if (!deviceFound) {
        clearPrintersConfig(printerType);
        return;
      }
      setSelectedDevices((prevState) => ({
        ...prevState,
        [printerType]: {
          ...prevState[printerType],
          device: deviceFound,
          status: 'ready',
        },
      }));
    }
  };

  /* Retrieve unitLabel devices */
  useEffect(() => {
    retrieveDevices('unitLabel');
  }, [deviceList, selectedDevices.unitLabel.config]);

  /* Retrieve hangTag devices */
  useEffect(() => {
    retrieveDevices('hangTag');
  }, [deviceList, selectedDevices.hangTag.config]);

  /* Retrieve cartonLabel devices */
  useEffect(() => {
    retrieveDevices('cartonLabel');
  }, [deviceList, selectedDevices.cartonLabel.config]);

  const setSelectedDevice = (
    printer: PrinterType,
    newConfig: BrowserPrintConfig,
  ) => {
    localStorage.setItem(`${printer}PrinterConfig`, JSON.stringify(newConfig));
    setSelectedDevices((prevState) => ({
      ...prevState,
      [printer]: { config: newConfig, device: null, status: 'selected' },
    }));
  };

  const sendZplCmd = async (
    userId: string,
    printerType: PrinterType,
    zplCmd: string,
    requestId: string,
    successCallback?: () => void,
    errorCallback?: (error: any) => void,
  ): Promise<void> => {
    const { device, status } = selectedDevices[printerType];

    if (!device) {
      updatePrintRequestStatus.mutate({
        userId,
        requestId,
        status: PrintRequestStatus.SENT_TO_PRINTER_FAIL,
        error: 'No printer selected.',
      });
      throw new Error('No printer selected.');
    }

    if (status === 'offline') {
      updatePrintRequestStatus.mutate({
        userId,
        requestId,
        status: PrintRequestStatus.SENT_TO_PRINTER_FAIL,
        error: 'Printer is offline.',
      });
      throw new Error('Printer is offline.');
    }

    updatePrintRequestStatus.mutate({
      userId,
      requestId,
      status: PrintRequestStatus.SENDING_TO_PRINTER,
    });

    return new Promise<void>((resolve, reject) => {
      device.send(
        zplCmd,
        async () => {
          try {
            await updatePrintRequestStatus.mutateAsync({
              userId,
              requestId,
              status: PrintRequestStatus.SENT_TO_PRINTER_OK,
            });
          } catch (statusError) {
            console.error(
              `Error updating print status for request: ${requestId}`,
              statusError,
            );
          }

          try {
            if (successCallback) successCallback();
          } catch (callbackError) {
            console.error(
              `Error in success callback for request: ${requestId}`,
              callbackError,
            );
          }

          resolve();
        },
        async (error) => {
          try {
            await updatePrintRequestStatus.mutateAsync({
              userId,
              requestId,
              status: PrintRequestStatus.SENT_TO_PRINTER_FAIL,
              error,
            });
          } catch (statusError) {
            console.error(
              `Error updating print status for request: ${requestId}`,
              statusError,
            );
          }

          try {
            if (errorCallback) errorCallback(error);
          } catch (callbackError) {
            console.error(
              `Error in error callback for request: ${requestId}`,
              callbackError,
            );
          }

          reject(new Error(`Print failed: ${error}`));
        },
      );
    });
  };

  return {
    deviceList,
    loading,
    selectedDevices,
    setSelectedDevice,
    clearPrintersConfig,
    sendZplCmd,
  };
};

export { useBrowserPrint };
